import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, UserPlus, Mail, Shield, Stethoscope, UserCheck, FlaskConical,
  Search, Filter, MoreVertical, CheckCircle, XCircle, Clock, RefreshCw,
  Send, Trash2, Edit, Eye, ChevronDown, AlertCircle, Copy, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Alert, AlertDescription } from '@/components/ui/alert';
import api from '@/lib/api';
import { toast } from 'sonner';

const ROLE_CONFIG = {
  admin: { name: 'Administrator', icon: Shield, color: 'bg-purple-100 text-purple-700 border-purple-200' },
  doctor: { name: 'Doctor', icon: Stethoscope, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  reception: { name: 'Receptionist', icon: UserCheck, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  lab_tech: { name: 'Lab Technician', icon: FlaskConical, color: 'bg-amber-100 text-amber-700 border-amber-200' }
};

const STATUS_CONFIG = {
  pending: { color: 'bg-amber-100 text-amber-700', icon: Clock },
  accepted: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  expired: { color: 'bg-slate-100 text-slate-600', icon: XCircle },
  revoked: { color: 'bg-rose-100 text-rose-700', icon: XCircle }
};

export default function UserManagement() {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [stats, setStats] = useState({});
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Dialog states
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [viewPermissionsDialogOpen, setViewPermissionsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  // Invite form
  const [inviteForm, setInviteForm] = useState({
    email: '',
    full_name: '',
    role: '',
    department: '',
    specialization: ''
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const [usersRes, invitationsRes, statsRes, rolesRes] = await Promise.all([
        api.get('/user-management/users'),
        api.get('/user-management/invitations'),
        api.get('/user-management/stats'),
        api.get('/user-management/roles')
      ]);
      setUsers(usersRes.data);
      setInvitations(invitationsRes.data);
      setStats(statsRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!inviteForm.email || !inviteForm.full_name || !inviteForm.role) {
      toast.error('Please fill in all required fields');
      return;
    }

    setInviteLoading(true);
    try {
      const response = await api.post('/user-management/invite', inviteForm);
      toast.success(
        'Invitation created! Use the copy button to share the magic link if email delivery fails.',
        { duration: 5000 }
      );
      setInviteDialogOpen(false);
      setInviteForm({ email: '', full_name: '', role: '', department: '', specialization: '' });
      
      // Auto-copy the invite link to clipboard
      const inviteLink = `${window.location.origin}/onboard?token=${response.data.token}`;
      try {
        await navigator.clipboard.writeText(inviteLink);
        toast.info('Magic link copied to clipboard!', { duration: 3000 });
      } catch (e) {
        // Clipboard may not be available
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRevokeInvitation = async (invitationId) => {
    try {
      await api.delete(`/user-management/invitations/${invitationId}`);
      toast.success('Invitation revoked');
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke invitation');
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      await api.post(`/user-management/invitations/${invitationId}/resend`);
      toast.success('Invitation resent successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to resend invitation');
    }
  };

  const handleDeactivateUser = async (userId) => {
    try {
      await api.put(`/user-management/users/${userId}/deactivate`);
      toast.success('User deactivated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate user');
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await api.put(`/user-management/users/${userId}/activate`);
      toast.success('User activated');
      fetchData();
    } catch (error) {
      toast.error('Failed to activate user');
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await api.put(`/user-management/users/${userId}/role?new_role=${newRole}`);
      toast.success('Role updated successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change role');
    }
  };

  const copyInviteLink = (token) => {
    const link = `${window.location.origin}/onboard?token=${token}`;
    navigator.clipboard.writeText(link);
    toast.success('Invitation link copied!');
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // Filter invitations
  const filteredInvitations = invitations.filter(inv => {
    const matchesSearch = inv.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  // Access Denied UI for non-admin users
  if (accessDenied) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md w-full border-2 border-rose-200">
          <CardContent className="p-8 text-center">
            <div className="h-16 w-16 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-rose-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-600 mb-4">
              You don't have permission to access User Management. This section is restricted to administrators only.
            </p>
            <Button
              onClick={() => window.history.back()}
              className="bg-[#4A238F] hover:bg-[#3A1B72] text-white"
            >
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="user-management-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
              User Management
            </h1>
            <p className="text-slate-600 mt-2">Manage users, roles, and invitations</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button
              className="bg-[#4A238F] hover:bg-[#3A1B72] text-white"
              onClick={() => setInviteDialogOpen(true)}
              data-testid="invite-user-button"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Invite User
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <Card className="border border-purple-200 bg-purple-50">
            <CardContent className="p-4 text-center">
              <Users className="h-6 w-6 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-purple-700">{stats.total_users || 0}</p>
              <p className="text-xs text-purple-600">Total Users</p>
            </CardContent>
          </Card>
          <Card className="border border-emerald-200 bg-emerald-50">
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-emerald-700">{stats.active_users || 0}</p>
              <p className="text-xs text-emerald-600">Active</p>
            </CardContent>
          </Card>
          <Card className="border border-amber-200 bg-amber-50">
            <CardContent className="p-4 text-center">
              <Clock className="h-6 w-6 text-amber-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-amber-700">{stats.pending_invitations || 0}</p>
              <p className="text-xs text-amber-600">Pending</p>
            </CardContent>
          </Card>
          {Object.entries(stats.by_role || {}).map(([role, count]) => {
            const RoleIcon = ROLE_CONFIG[role]?.icon || Users;
            return (
              <Card key={role} className={`border ${ROLE_CONFIG[role]?.color || 'border-slate-200'}`}>
                <CardContent className="p-4 text-center">
                  <RoleIcon className="h-6 w-6 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-xs">{ROLE_CONFIG[role]?.name || role}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-slate-100 p-1.5 rounded-xl border border-slate-200 h-auto flex-wrap">
            <TabsTrigger 
              value="users" 
              className="data-[state=active]:bg-[#4A238F] data-[state=active]:text-white data-[state=inactive]:bg-transparent data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-slate-200 px-4 py-2 rounded-lg font-medium transition-all"
            >
              <Users className="mr-2 h-4 w-4" />
              Users ({filteredUsers.length})
            </TabsTrigger>
            <TabsTrigger 
              value="invitations"
              className="data-[state=active]:bg-[#4A238F] data-[state=active]:text-white data-[state=inactive]:bg-transparent data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-slate-200 px-4 py-2 rounded-lg font-medium transition-all"
            >
              <Mail className="mr-2 h-4 w-4" />
              Invitations ({filteredInvitations.length})
            </TabsTrigger>
            <TabsTrigger 
              value="roles"
              className="data-[state=active]:bg-[#4A238F] data-[state=active]:text-white data-[state=inactive]:bg-transparent data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-slate-200 px-4 py-2 rounded-lg font-medium transition-all"
            >
              <Shield className="mr-2 h-4 w-4" />
              Roles & Permissions
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            {/* Filters */}
            <Card className="mb-4 border border-slate-200">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search by name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="user-search-input"
                    />
                  </div>
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filter by role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      {Object.entries(ROLE_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Users List */}
            <div className="space-y-3">
              {filteredUsers.length === 0 ? (
                <Card className="border border-slate-200">
                  <CardContent className="p-12 text-center">
                    <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No users found</p>
                  </CardContent>
                </Card>
              ) : (
                filteredUsers.map((user, index) => {
                  const roleConfig = ROLE_CONFIG[user.role] || {};
                  const RoleIcon = roleConfig.icon || Users;
                  return (
                    <motion.div
                      key={user.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                    >
                      <Card className={`border shadow-sm hover:shadow-md transition-all ${!user.is_active ? 'opacity-60' : ''}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`h-12 w-12 rounded-full flex items-center justify-center ${roleConfig.color || 'bg-slate-100'}`}>
                                <RoleIcon className="h-6 w-6" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-slate-900">{user.full_name}</h3>
                                  <Badge className={roleConfig.color}>{roleConfig.name || user.role}</Badge>
                                  {!user.is_active && (
                                    <Badge variant="outline" className="text-rose-600 border-rose-200">Inactive</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600">{user.email}</p>
                                {user.department && (
                                  <p className="text-xs text-slate-500">{user.department}</p>
                                )}
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => {
                                  setSelectedRole(roles.find(r => r.id === user.role));
                                  setViewPermissionsDialogOpen(true);
                                }}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Permissions
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit User
                                </DropdownMenuItem>
                                {Object.keys(ROLE_CONFIG).filter(r => r !== user.role).map(role => (
                                  <DropdownMenuItem key={role} onClick={() => handleChangeRole(user.id, role)}>
                                    Change to {ROLE_CONFIG[role].name}
                                  </DropdownMenuItem>
                                ))}
                                <DropdownMenuSeparator />
                                {user.is_active ? (
                                  <DropdownMenuItem
                                    className="text-rose-600"
                                    onClick={() => handleDeactivateUser(user.id)}
                                  >
                                    <XCircle className="mr-2 h-4 w-4" />
                                    Deactivate
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem
                                    className="text-emerald-600"
                                    onClick={() => handleActivateUser(user.id)}
                                  >
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    Activate
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Invitations Tab */}
          <TabsContent value="invitations">
            {/* Filters */}
            <Card className="mb-4 border border-slate-200">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search invitations..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="accepted">Accepted</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                      <SelectItem value="revoked">Revoked</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Invitations List */}
            <div className="space-y-3">
              {filteredInvitations.length === 0 ? (
                <Card className="border border-slate-200">
                  <CardContent className="p-12 text-center">
                    <Mail className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No invitations found</p>
                    <Button
                      className="mt-4 bg-[#4A238F] hover:bg-[#3A1B72] text-white"
                      onClick={() => setInviteDialogOpen(true)}
                    >
                      <UserPlus className="mr-2 h-4 w-4" />
                      Send First Invitation
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                filteredInvitations.map((invitation, index) => {
                  const roleConfig = ROLE_CONFIG[invitation.role] || {};
                  const statusConfig = STATUS_CONFIG[invitation.status] || {};
                  const StatusIcon = statusConfig.icon || Clock;
                  return (
                    <motion.div
                      key={invitation.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                    >
                      <Card className="border shadow-sm">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`h-12 w-12 rounded-full flex items-center justify-center ${statusConfig.color}`}>
                                <StatusIcon className="h-6 w-6" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-slate-900">{invitation.full_name}</h3>
                                  <Badge className={roleConfig.color}>{roleConfig.name || invitation.role}</Badge>
                                  <Badge className={statusConfig.color}>{invitation.status}</Badge>
                                </div>
                                <p className="text-sm text-slate-600">{invitation.email}</p>
                                <p className="text-xs text-slate-500">
                                  Invited by {invitation.invited_by_name} • Expires: {new Date(invitation.expires_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {invitation.status === 'pending' && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => copyInviteLink(invitation.token)}
                                    title="Copy invite link"
                                  >
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleResendInvitation(invitation.id)}
                                    title="Resend invitation"
                                  >
                                    <Send className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-rose-600 hover:bg-rose-50"
                                    onClick={() => handleRevokeInvitation(invitation.id)}
                                    title="Revoke invitation"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Roles & Permissions Tab */}
          <TabsContent value="roles">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {roles.map((role) => {
                const config = ROLE_CONFIG[role.id] || {};
                const RoleIcon = config.icon || Users;
                return (
                  <Card key={role.id} className={`border-2 ${config.color?.replace('bg-', 'border-').split(' ')[0] || 'border-slate-200'}`}>
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${config.color}`}>
                          <RoleIcon className="h-5 w-5" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{role.name}</CardTitle>
                          <CardDescription className="text-xs">{role.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm font-medium text-slate-700 mb-2">Module Access:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(role.permissions || {}).map(([module, access]) => (
                          <Badge
                            key={module}
                            variant="outline"
                            className="text-xs py-0.5"
                          >
                            {module.replace(/_/g, ' ')}
                            {access !== 'full' && access !== true && (
                              <span className="text-slate-400 ml-1">({access})</span>
                            )}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>

        {/* Invite User Dialog */}
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-[#4A238F]" />
                Invite New User
              </DialogTitle>
              <DialogDescription>
                Send an invitation email with a secure magic link for onboarding.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleInviteUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="user@hospital.com"
                  required
                  data-testid="invite-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  value={inviteForm.full_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                  placeholder="Dr. John Smith"
                  required
                  data-testid="invite-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role *</Label>
                <Select
                  value={inviteForm.role}
                  onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}
                >
                  <SelectTrigger data-testid="invite-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <config.icon className="h-4 w-4" />
                          {config.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Input
                  id="department"
                  value={inviteForm.department}
                  onChange={(e) => setInviteForm({ ...inviteForm, department: e.target.value })}
                  placeholder="e.g., Cardiology, Emergency"
                />
              </div>

              {inviteForm.role === 'doctor' && (
                <div className="space-y-2">
                  <Label htmlFor="specialization">Specialization</Label>
                  <Input
                    id="specialization"
                    value={inviteForm.specialization}
                    onChange={(e) => setInviteForm({ ...inviteForm, specialization: e.target.value })}
                    placeholder="e.g., Cardiologist, Surgeon"
                  />
                </div>
              )}

              <Alert className="bg-purple-50 border-purple-200">
                <Mail className="h-4 w-4 text-purple-600" />
                <AlertDescription className="text-purple-700">
                  A secure magic link will be sent to the user's email. The link expires in 24 hours.
                </AlertDescription>
              </Alert>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={inviteLoading}
                  className="bg-[#4A238F] hover:bg-[#3A1B72] text-white"
                  data-testid="send-invite-button"
                >
                  {inviteLoading ? 'Sending...' : 'Send Invitation'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Permissions Dialog */}
        <Dialog open={viewPermissionsDialogOpen} onOpenChange={setViewPermissionsDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {selectedRole?.name} Permissions
              </DialogTitle>
              <DialogDescription>
                {selectedRole?.description}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {selectedRole?.permissions && Object.entries(selectedRole.permissions).map(([module, access]) => (
                <div key={module} className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="font-medium text-slate-700 capitalize">
                    {module.replace(/_/g, ' ')}
                  </span>
                  <Badge variant="outline">
                    {access === true || access === 'full' ? 'Full Access' : access}
                  </Badge>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}
